import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [animationStep, setAnimationStep] = useState(0);
  const navigate = useNavigate();

  const flowSteps = [
    { 
      id: 1, 
      label: "Requirements", 
      icon: "FileQuestion", 
      color: "text-blue-400",
      description: "Contextual questions"
    },
    { 
      id: 2, 
      label: "Analysis", 
      icon: "GitCompare", 
      color: "text-purple-400",
      description: "Fit/Gap assessment"
    },
    { 
      id: 3, 
      label: "Documents", 
      icon: "FileText", 
      color: "text-accent",
      description: "BPD² & FSD² generation"
    },
    { 
      id: 4, 
      label: "Approval", 
      icon: "PenTool", 
      color: "text-green-400",
      description: "Digital signoffs"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % 4);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    navigate('/user-registration');
  };

  const handleViewDemo = () => {
    navigate('/app-dashboard');
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 border border-accent/30 rounded-full"></div>
        <div className="absolute top-40 right-20 w-24 h-24 border border-accent/20 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-accent/40 rounded-full"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-accent text-sm font-medium mb-6">
              <Icon name="Sparkles" size={16} className="mr-2" />
              Streamline SAP Implementation
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
              Transform SAP
              <span className="block text-accent">Requirements</span>
              into Solutions
            </h1>
            
            <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl">
              Guide contextual requirement discussions, perform automated fit/gap analysis, and generate digitally signed BPD² and FSD² documents to accelerate enterprise solution design approval cycles.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                variant="default"
                size="lg"
                onClick={handleGetStarted}
                className="bg-accent text-primary hover:bg-accent/90 px-8 py-4 text-lg font-semibold"
                iconName="ArrowRight"
                iconPosition="right"
              >
                Start an Engagement
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={handleViewDemo}
                className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 px-8 py-4 text-lg"
                iconName="Play"
                iconPosition="left"
              >
                View Demo
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-primary-foreground/20">
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-accent">500+</div>
                <div className="text-sm text-primary-foreground/70">SAP Best Practices</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-accent">85%</div>
                <div className="text-sm text-primary-foreground/70">Faster Implementation</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-accent">100%</div>
                <div className="text-sm text-primary-foreground/70">Digital Compliance</div>
              </div>
            </div>
          </div>

          {/* Animated Flow Visualization */}
          <div className="relative">
            <div className="bg-card/10 backdrop-blur-sm border border-accent/20 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-primary-foreground mb-6 text-center">
                Automated Workflow
              </h3>
              
              <div className="space-y-6">
                {flowSteps?.map((step, index) => (
                  <div key={step?.id} className="flex items-center space-x-4">
                    {/* Step Icon */}
                    <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                      animationStep >= index 
                        ? `border-accent bg-accent/20 ${step?.color}` 
                        : 'border-primary-foreground/30 text-primary-foreground/50'
                    }`}>
                      <Icon 
                        name={step?.icon} 
                        size={20} 
                        className={animationStep >= index ? step?.color : 'text-primary-foreground/50'}
                      />
                    </div>

                    {/* Step Content */}
                    <div className="flex-1">
                      <div className={`font-medium transition-colors duration-500 ${
                        animationStep >= index ? 'text-primary-foreground' : 'text-primary-foreground/60'
                      }`}>
                        {step?.label}
                      </div>
                      <div className="text-sm text-primary-foreground/60">
                        {step?.description}
                      </div>
                    </div>

                    {/* Progress Line */}
                    {index < flowSteps?.length - 1 && (
                      <div className="absolute left-[72px] mt-12 w-0.5 h-6 bg-gradient-to-b from-accent/40 to-transparent"></div>
                    )}
                  </div>
                ))}
              </div>

              {/* Completion Animation */}
              <div className={`mt-6 p-4 bg-accent/10 border border-accent/30 rounded-lg transition-all duration-500 ${
                animationStep === 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-accent" />
                  <span className="text-primary-foreground font-medium">
                    Ready for Enterprise Deployment
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;